<template>
  <div class="AtlasTabs">
    <div class="AtlasTabs-tabs" v-if="tabs.length">
      <div
        v-for="(tab, i) in tabs"
        :id="[`tour-tab${i + 1}`]"
        :key="tab.hed"
        class="AtlasTabs-tab"
        :class="{ 'is-active': active === i }"
        @click="selectTab(i)"
      >
        {{ tab.hed }}
      </div>
      <div
        v-if="this.indicatorConfig.tourButtonText"
        class="AtlasTabs-tab AtlasTabs-tab-button"
        @click="activateTour()"
      >
        {{ this.indicatorConfig.tourButtonText }}
      </div>
    </div>

    <div
      v-for="(tab, i) in tabs"
      :key="tab.hed"
      class="AtlasTabs-content"
      :class="{ 'is-hidden': active !== i }"
      :ref="`content-${i}`"
    >
      <strong v-if="tab.subhed">{{ tab.subhed }}</strong>
      <template v-if="tab.subhed">&nbsp;</template>
      <span v-html="sanitize(tab.caption)"></span>
    </div>
  </div>
</template>

<script>
import sanitize from 'sanitize-html';
import { mapGetters } from 'vuex';

export default {
  emits: ['update:tabHeight'],

  props: {
    breakdownId: {
      type: String,
      required: true,
    },
    tabHeight: {
      type: Number,
      default: 0,
    },
  },

  data: () => ({
    active: -1,
  }),

  computed: {
    ...mapGetters('indicator', ['indicatorConfig']),

    ...mapGetters('breakdown', ['breakdownById', 'captionByBreakdownId']),

    breakdown() {
      return this.breakdownById(this.breakdownId);
    },
    caption() {
      return this.captionByBreakdownId(this.breakdownId);
    },

    whatItShows() {
      if (!this.caption) return undefined;

      return {
        hed: this.breakdown.captionHed || 'What it shows',
        subhed: this.breakdown.captionSubhed,
        caption: this.caption,
      };
    },

    whyItMatters() {
      if (!this.indicatorConfig.caption) return undefined;

      return {
        hed: this.indicatorConfig.captionHed || 'Why it matters',
        subhed: this.indicatorConfig.captionSubhed,
        caption: this.indicatorConfig.caption,
      };
    },

    tabs() {
      return [this.whatItShows, this.whyItMatters].filter((x) => x);
    },
  },

  methods: {
    sanitize,

    selectTab(i) {
      this.active = this.active === i ? -1 : i;
      this.emitHeight();
    },

    activateTour() {
      this.$router.push(`?tour`);
    },

    emitHeight() {
      const activeTab = this.$refs[`content-${this.active}`];

      const height = (activeTab || {}).offsetHeight || 0;

      this.$emit('update:tabHeight', height);
    },
  },

  mounted() {
    this.emitHeight();
    window.addEventListener('resize', this.emitHeight);
  },

  unmounted() {
    window.removeEventListener('resize', this.emitHeight);
  },
};
</script>

<style lang="scss">
.AtlasTabs {
  &-tabs {
    white-space: nowrap;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    @include shadow-strip(top) {
      z-index: 2;
    }
  }

  &-tab {
    position: relative;
    z-index: 1;
    display: inline-block;
    padding: 16px $gutter 13px;
    margin-right: 2px;
    background-color: color(bg);
    color: color(disabled);
    font-size: 16px;
    font-weight: bold;
    line-height: 1;
    text-transform: uppercase;

    &:hover {
      color: color(black);
      cursor: pointer;
    }

    &.is-active {
      z-index: 3;
      color: color(black);
    }

    &-button {
      color: red !important;

      &:hover {
        color: color(black) !important;
        cursor: pointer !important;
      }
    }
  }

  &-content {
    @include shadow-strip(top);
    width: 100%;
    padding: 18px $gutter 22px;
    background: color(bg);
    font-size: 20px;
    line-height: 1.5;

    &.is-hidden {
      @include position(absolute, -999999px null null -999999px);
      visibility: hidden;
    }
  }
}
</style>
